// Raccoglie e normalizza i dati del report (sola lettura) per il generatore PDF
const API_URL = process.env.REACT_APP_BACKEND_URL;
const TZ = 'Europe/Rome';

const dayKeyFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
export const dayKey = (date) => dayKeyFmt.format(new Date(date));

const MEAL_TYPES = {
  colazione: 'Colazione', breakfast: 'Colazione',
  pranzo: 'Pranzo', lunch: 'Pranzo',
  merenda: 'Merenda', snack: 'Merenda', spuntino: 'Merenda',
  cena: 'Cena', dinner: 'Cena',
};
export const COUNTED_MEALS = ['Colazione', 'Pranzo', 'Cena'];

const ACTIVITY_NAMES = {
  passeggiata: 'Passeggiata', walk: 'Passeggiata', walking: 'Passeggiata',
  igiene: 'Igiene e cura personale', hygiene: 'Igiene e cura personale',
  riposo: 'Riposo', rest: 'Riposo', nap: 'Riposo',
  esercizi: 'Esercizi', exercise: 'Esercizi', exercises: 'Esercizi',
  altro: 'Attività',
};

const SERENE = ['lucido', 'presente', 'lucid', 'present', 'oriented', 'chiacchierone', 'socievole', 'talkative', 'social', 'chatty',
  'calmo', 'rilassato', 'calm', 'relaxed', 'peaceful', 'sereno', 'serena', 'felice', 'happy', 'joyful', 'content'];
const SAD = ['triste', 'giu', 'giù', 'sad', 'down', 'depressed'];

const clean = (text) => (text == null ? '' : String(text)
  .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}\u{2B00}-\u{2BFF}]/gu, '')
  .replace(/\s+/g, ' ')
  .trim());

const humanize = (value) => {
  const text = clean(value).replace(/_/g, ' ');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};

export const moodLevel = (detail, categoria) => {
  if (categoria && categoria !== 'umore') return null;
  const value = clean(detail).toLowerCase();
  if (SERENE.includes(value)) return 'serena';
  if (SAD.includes(value)) return 'triste';
  return 'neutra';
};

const getJson = async (url, headers) => {
  try {
    const response = await fetch(url, { headers });
    return response.ok ? await response.json() : null;
  } catch (err) {
    return null;
  }
};

const mostFrequent = (values) => {
  const counts = {};
  values.filter(Boolean).forEach((v) => { counts[v] = (counts[v] || 0) + 1; });
  return Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || null;
};

// Converte la timeline e le altre fonti in un modello unico, filtrato sul periodo
export const buildReportModel = ({ famiglia, startDate, endDate, timeline = [], schedule = [], visits = [], walks = [] }) => {
  const dayKeys = [];
  for (let d = new Date(startDate); dayKey(d) <= dayKey(endDate); d = new Date(d.getTime() + 86400000)) {
    if (!dayKeys.includes(dayKey(d))) dayKeys.push(dayKey(d));
  }
  const inRange = (t) => t && dayKeys.includes(dayKey(t));

  const doseByName = {};
  schedule.forEach((m) => {
    const name = clean(m.nome_farmaco || m.nome).toLowerCase();
    if (name && m.dosaggio && !doseByName[name]) doseByName[name] = clean(m.dosaggio);
  });
  const doseFor = (name, given) => clean(given) || doseByName[clean(name).toLowerCase()] || '';

  const model = {
    patientName: clean(famiglia?.nome_assistito) || 'Assistito',
    familyName: clean(famiglia?.nome_famiglia || famiglia?.nome) || '',
    startDate, endDate, dayKeys,
    expectedDosesPerDay: schedule.length,
    medicines: [], meals: [], activities: [], moods: [], notes: [], sos: [], visits: [], walks: [], truncated: [],
  };
  const caregivers = [];

  timeline.filter((item) => inRange(item.timestamp)).forEach((item) => {
    const time = new Date(item.timestamp);
    const type = (item.type || '').toLowerCase();
    const note = clean(item.note_text);
    caregivers.push(clean(item.assistente_nome));
    if (type === 'medicine' && Array.isArray(item.farmaci) && item.farmaci.length) {
      item.farmaci.forEach((f) => model.medicines.push({ time, name: clean(f.nome) || 'Terapia', dose: doseFor(f.nome, f.dosaggio), note }));
    } else if (type === 'medicine') {
      const name = clean(item.medicine_name || item.detail) || 'Terapia';
      model.medicines.push({ time, name, dose: doseFor(name), note });
    } else if (type === 'meal') {
      const key = clean(item.detail).toLowerCase();
      model.meals.push({ time, type: MEAL_TYPES[key] || 'Pasto', description: note });
    } else if (type === 'wellness') {
      const key = clean(item.detail).toLowerCase();
      const name = key === 'altro' && note ? note : ACTIVITY_NAMES[key] || humanize(item.detail) || 'Attività';
      model.activities.push({ time, name, note: name === note ? '' : note });
    } else if (type === 'mood') {
      const level = moodLevel(item.detail, item.status_categoria);
      model.moods.push({ time, level, label: humanize(item.detail), categoria: item.status_categoria || 'umore', note });
    } else if (type === 'sos') {
      model.sos.push({ time, kind: item.is_sos ? 'Scudo' : 'SOS', description: clean(item.detail || item.note_text) || 'Allarme' });
    } else if (type === 'note') {
      model.notes.push({ time, text: note || clean(item.detail) });
    }
  });

  model.visits = visits.filter((v) => inRange(v.data_ora)).map((v) => ({
    time: new Date(v.data_ora),
    doctor: clean(v.specialista?.nome) || clean(v.titolo) || 'Visita',
    specialty: clean(v.specialista?.ruolo) || humanize(v.tipo_visita),
    outcome: clean(v.note),
    title: clean(v.titolo),
  }));

  model.walks = walks.filter((w) => inRange(w.started_at)).map((w) => {
    caregivers.push(clean(w.assistente_nome));
    const pins = (w.pins || []).filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    return {
      start: new Date(w.started_at),
      end: w.ended_at ? new Date(w.ended_at) : null,
      minutes: w.duration_minutes,
      pins,
      zone: w.zone || [],
      place: clean(pins.find((p) => p.place)?.place),
    };
  }).sort((a, b) => a.start - b.start);

  ['medicines', 'meals', 'activities', 'moods', 'notes', 'sos', 'visits'].forEach((k) => model[k].sort((a, b) => a.time - b.time));
  model.caregiverName = mostFrequent(caregivers);
  return model;
};

const TRUNCATION_LABELS = {
  logs: 'pasti, attività, umore e note', medicine_logs: 'medicine', sos_alerts: 'allarmi Scudo', walks: 'passeggiate', visits: 'visite',
};

// Tutto il periodo filtrato per data lato server (con un giorno di margine per il fuso orario)
export const fetchReportModel = async ({ famiglia, startDate, endDate }) => {
  const id = famiglia.id;
  const from = new Date(new Date(startDate).getTime() - 86400000).toISOString();
  const to = new Date(new Date(endDate).getTime() + 86400000).toISOString();
  const headers = famiglia.token ? { 'X-Family-Token': famiglia.token } : undefined;
  const [periodData, scheduleData] = await Promise.all([
    getJson(`${API_URL}/api/dashboard/${id}/report-data?start=${encodeURIComponent(from)}&end=${encodeURIComponent(to)}`, headers),
    getJson(`${API_URL}/api/dashboard/${id}/medicines`),
  ]);
  if (!periodData) throw new Error('Dati del report non disponibili');
  const model = buildReportModel({
    famiglia, startDate, endDate,
    timeline: periodData.timeline || [],
    schedule: scheduleData?.source === 'medicine_schedule' ? scheduleData.medicines || [] : [],
    visits: periodData.visite || [],
    walks: periodData.walks || [],
  });
  model.truncated = Object.keys(TRUNCATION_LABELS).filter((k) => periodData.truncated?.[k]).map((k) => TRUNCATION_LABELS[k]);
  model.rowLimit = periodData.row_limit;
  return model;
};
